import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import Button from '../Button';
import {
  EnteredInputs,
  ReactModalButton,
  StandardModalContent,
} from './ModalStackContext';
import styles from './StandardModalButtons.module.css';

type StandardModalButtonsProps = {
  cancelButton?: StandardModalContent['cancelButton'];
  /** `HTMLButtonElement` props to apply to the Ok button */
  cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /**
   * Callback function to run whenever the Cancel button is clicked
   *
   * If `null`, nothing will happen
   *  */
  onCancel: (() => any | Promise<any>) | null;
  okButton?: StandardModalContent['okButton'];
  /** `HTMLButtonElement` props to apply to the Ok button */
  okButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /**
   * Callback function to run whenever the OK button is clicked
   *
   * If `null`, nothing will happen
   *
   * If the `content` parameter contains input fields, they can be put into an object and sent within the `inputValues` parameter in the callback
   * */
  onOk: ((inputValues?: EnteredInputs) => any | Promise<any>) | null;
  inputs?: StandardModalContent['inputs'];
  inputValues?: EnteredInputs;
};

/** Standard actions/buttons footer for a modal */
export default function StandardModalButtons(
  props: StandardModalButtonsProps,
): React.ReactElement {
  const {
    cancelButton,
    cancelButtonProps,
    onCancel,
    okButton,
    okButtonProps,
    onOk,
    inputs,
    inputValues = {},
  } = props;

  const requiredInputsComplete = useMemo(
    () =>
      inputs && !isEmpty(inputs)
        ? inputs
            .filter((input) => input.required)
            .every((input) => !isEmpty(inputValues[input.id]))
        : true,
    [inputValues, inputs],
  );

  function buildOkButton(
    okButton: string | ReactModalButton | null | undefined,
    onOk: ((inputValues?: EnteredInputs) => void) | null,
  ): React.ReactNode {
    const handleClick = () => {
      if (onOk && inputValues) {
        onOk(inputValues);
      } else {
        onOk?.();
      }
    };
    const onClick = onOk && inputValues ? () => onOk(inputValues) : onOk;

    if (okButton === undefined) {
      return (
        <Button
          onClick={onClick}
          disabled={!!okButtonProps?.disabled || !requiredInputsComplete}
          {...okButtonProps}
        >
          OK
        </Button>
      );
    } else if (typeof okButton === 'string') {
      return (
        <Button
          onClick={onClick}
          disabled={!!okButtonProps?.disabled || !requiredInputsComplete}
          {...okButtonProps}
        >
          {okButton}
        </Button>
      );
    } else if (React.isValidElement(okButton)) {
      return React.cloneElement(okButton, {
        onClick: handleClick,
        disabled: !!okButtonProps?.disabled || !requiredInputsComplete,
      });
    }
    return null;
  }

  function buildCancelButton(
    cancelButton: string | ReactModalButton | null | undefined,
    onCancel: (() => void) | null,
  ): React.ReactNode {
    if (cancelButton === undefined) {
      return (
        <Button type="outline" onClick={onCancel} {...cancelButtonProps}>
          Cancel
        </Button>
      );
    } else if (typeof cancelButton === 'string') {
      return (
        <Button type="outline" onClick={onCancel} {...cancelButtonProps}>
          {cancelButton}
        </Button>
      );
    } else if (React.isValidElement(cancelButton)) {
      const onClick = onCancel ? onCancel : cancelButton.props.onClick;
      return React.cloneElement(cancelButton, { onClick });
    }
    return null;
  }

  return (
    <div className={styles.actions}>
      {buildCancelButton(cancelButton, onCancel)}
      {buildOkButton(okButton, onOk)}
    </div>
  );
}
